import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { Dialog } from 'react-toolbox';
import { Button, IconButton } from 'react-toolbox/lib/button';
import { connect } from 'react-redux';
import styles from 'styles/input.scss';
import StyledSelect from 'components/StyledSelect/react_select';
import S from 'modal.scss';
import { assignPhaseEstimator, requestWorkroomList, requestEstimatorListByWorkroom,
         clearValidationErrors } from 'actionCreators';
import PhaseType from 'types/phase';
import { getPhaseErrors, getWorkroomList } from '../../../../../../reducers';
import { generateSource } from '../../../../../../utils';
import { SCHEDULE_DATE, DISPLAY_DATE,
         RAILS_DATE, RAILS_DATE_ALT } from 'config/constants';

const REQUIRED_ERROR_MSG = 'Can\'t be blank.';
const DEFAULT_STATE = {
  selectedWorkroom: '',
  selectedEstimator: '',
  loading: false,
  errors: {},
};
const DAY_MAP = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};
const BASE_AVAILABILITY_WARNING = 'no availability for the phase store';

const dayExcluded = (timeArr, day) => !timeArr.includes(day.toLowerCase());

class AssignEstimatorModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.defaultState();
  }

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.active && !this.state.active && nextProps.active !== this.props.active) {
      this.props.requestWorkroomList();
      if (this.state.selectedWorkroom) {
        this.props.requestEstimatorListByWorkroom(this.state.selectedWorkroom);
      }
    }
  }

  getDateFromNotes = (notes) => {
    if (!notes) return null;
    // Matches YYYY-M-D, YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY
    const regex = /(\d{4}-\d{1,2}-\d{1,2})|(\d{1,2}\/\d{1,2}\/\d{2,4})/m;
    const match = notes.match(regex);
    return match ? match[0] : null;
  }

  getMoment = (date, format) => moment(date, format).startOf('day');

  getNoteMoment = (notes) => {
    const noteDate = this.getDateFromNotes(notes);
    if (noteDate) {
      return this.getMoment(
        noteDate, [SCHEDULE_DATE, DISPLAY_DATE, RAILS_DATE, RAILS_DATE_ALT],
      );
    }
    return null;
  }

  getFormattedDate = momentObj => momentObj.format(DISPLAY_DATE);

  getAvailabilityWarningByDay = (notes, noteMoment, availability) => {
    let unavailable = false;
    const day = DAY_MAP[noteMoment.day()];
    const noteTime = (notes.match(/morning|afternoon|evening/im) || [''])[0].toLowerCase();
    if (noteTime) {
      const availabilityByTime = availability[noteTime] || [];
      if (dayExcluded(availabilityByTime, day)) unavailable = true;
    } else {
      const { morning = [], afternoon = [], evening = [] } = availability;
      unavailable = dayExcluded(morning, day) &&
                    dayExcluded(afternoon, day) &&
                    dayExcluded(evening, day);
    }
    return unavailable ? `${BASE_AVAILABILITY_WARNING} on ${this.getFormattedDate(noteMoment)}` : null;
  }

  getAvailabilityWarningByPto = (noteMoment, ptos) => (
    (ptos.length &&
    ptos.some(p => noteMoment.isSameOrAfter(this.getMoment(p.start_date, RAILS_DATE)) &&
    noteMoment.isSameOrBefore(this.getMoment(p.end_date, RAILS_DATE)))) ?
      `scheduled time off on ${this.getFormattedDate(noteMoment)}` : null
  );

  getAvailabilityWarning = () => {
    const { estimators, phase: { contractor_notes = '', store: { id: storeId } } } = this.props;
    const { selectedEstimator } = this.state;
    const notes = !contractor_notes || contractor_notes === null ? '' : contractor_notes;
    const noteMoment = this.getNoteMoment(notes);
    let warnWitoutDisable = false;
    if (selectedEstimator && noteMoment) {
      const warnings = [];
      const { ptos = [], assignee_availabilities = [] } =
        estimators.find(e => e.id === selectedEstimator) || {};
      const availability = assignee_availabilities.map(a => a.assignee_availability)
                                                  .find(a => a.store_id === storeId) || null;
      if (!availability) {
        warnings.push(BASE_AVAILABILITY_WARNING);
        warnWitoutDisable = true;
      } else {
        const dayWarning = this.getAvailabilityWarningByDay(notes, noteMoment, availability);
        if (dayWarning) {
          warnings.push(dayWarning);
          warnWitoutDisable = true;
        }
      }
      const ptoWarning = this.getAvailabilityWarningByPto(noteMoment, ptos);
      if (ptoWarning) warnings.push(ptoWarning);
      if (warnings.length) {
        return {
          warning: `Selected Estimator: ${warnings.join(', ')}`,
          warnWitoutDisable,
        };
      }
    }
    return '';
  }

  defaultState = () => ({
    ...DEFAULT_STATE,
    selectedWorkroom: this.props.phase.store.workroom_id,
    selectedEstimator: this.props.phase.estimator_id,
  })

  validate = () => {
    const { selectedEstimator, selectedWorkroom } = this.state;
    const errors = {};
    if (!selectedWorkroom || selectedWorkroom.toString().trim() === '') {
      errors.workroom = REQUIRED_ERROR_MSG;
    }
    if (!selectedEstimator || selectedEstimator.toString().trim() === '') {
      errors.estimator = REQUIRED_ERROR_MSG;
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return false;
    }
    return true;
  }

  handleWorkroomChange = (option) => {
    this.setState({ selectedWorkroom: option.value, selectedEstimator: '' });
    if (!(typeof option === 'string')) {
      this.props.requestEstimatorListByWorkroom(((option || {}).value || null).toString());
    }
  }

  handleEstimatorChange = option => this.setState({ selectedEstimator: option.value });

  handleSubmit = () => {
    if (!this.validate()) return;
    const phase = this.props.phase;
    const { selectedEstimator } = this.state;
    this.setState({ loading: true });
    this.props.assignPhaseEstimator(phase.id, { estimator_id: selectedEstimator })
      .then(() => this.handleClose())
      .catch(() => this.setState({ loading: false }));
  }

  handleClose = () => {
    this.setState(this.defaultState());
    this.props.clearValidationErrors('PHASE');
    this.props.onClose();
  }

  render = () => {
    const { active, workrooms, estimators, serverErrors } = this.props;
    const { errors, loading, selectedWorkroom, selectedEstimator } = this.state;
    const workroomOptions = generateSource(workrooms);
    const estimatorOptions = generateSource(estimators);
    const { warning: availabilityWarning, warnWitoutDisable } = (estimators || []).length ? this.getAvailabilityWarning() : { warning: '', warnWitoutDisable: false };
    return (<Dialog
      active={active}
      className={S.modal}
      type="fullscreen"
      onEscKeyDown={this.handleClose}
    >
      <div className={styles.errored}>
        {serverErrors && Object.keys(serverErrors).length > 0 &&
          <span className={styles.error}>This action could not be completed.</span>}
      </div>
      <IconButton
        icon="close"
        className={S.close}
        onClick={this.handleClose}
        tabIndex={-1}
      />
      <h5 className={S.title}>Assign Estimator</h5>
      <StyledSelect
        className={S.commSelect}
        label="Workroom *"
        onChange={this.handleWorkroomChange}
        options={workroomOptions}
        value={selectedWorkroom}
        error={errors.workroom}
      />
      <StyledSelect
        className={S.commSelect}
        placeholder="Not Assigned"
        label="Estimator Name *"
        onChange={this.handleEstimatorChange}
        options={estimatorOptions}
        value={selectedEstimator}
        error={errors.estimator}
      />
      <div className={S.singleAction}>
        {availabilityWarning && <p className={S.missingFieldsMessage}>{availabilityWarning}</p>}
        <Button
          label="Assign Estimator"
          flat inverse ripple
          className={S.action}
          onClick={this.handleSubmit}
          disabled={loading || (!!availabilityWarning && !warnWitoutDisable)}
        />
      </div>
    </Dialog>);
  }
}

AssignEstimatorModal.propTypes = {
  active: PropTypes.bool.isRequired,
  requestWorkroomList: PropTypes.func.isRequired,
  requestEstimatorListByWorkroom: PropTypes.func.isRequired,
  clearValidationErrors: PropTypes.func.isRequired,
  assignPhaseEstimator: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  workrooms: PropTypes.array.isRequired,
  estimators: PropTypes.array.isRequired,
  serverErrors: PropTypes.shape({}),
};

AssignEstimatorModal.defaultProps = { active: false };

export default connect(
  state => ({
    serverErrors: getPhaseErrors(state),
    workrooms: getWorkroomList(state),
    estimators: (state.phase.details.estimatorsByWorkroom || []),
  }), {
    assignPhaseEstimator,
    requestWorkroomList,
    requestEstimatorListByWorkroom,
    clearValidationErrors,
  },
)(AssignEstimatorModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/assignEstimatorModal.jsx